/**
 * 🔒 PASSWORD RE-AUTHENTICATION DIALOG FOR PII ACCESS
 * Requires admin to re-enter password before viewing sensitive documents
 * Issues short-lived JWT token (5 minutes) for PII access
 * All verification attempts logged to audit_logs
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Security as SecurityIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import apiClient from '@/services/apiClient';

export interface PIIAccessToken {
  token: string;
  expiresAt: string;
  expiresIn: number;
}

interface PasswordReAuthDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (piiToken: PIIAccessToken) => void;
  resourceType: 'photos' | 'documents' | 'verification_data';
  resourceId: string;
  title?: string;
  description?: string;
}

export const PasswordReAuthDialog: React.FC<PasswordReAuthDialogProps> = ({
  open,
  onClose,
  onSuccess,
  resourceType,
  resourceId,
  title = 'Password Verification Required',
  description = 'For security and compliance, please verify your identity before accessing sensitive user documents.',
}) => {
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for accessing this information');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call backend API to verify password and get PII access token
      const response = await apiClient.post('/api/v1/admin/auth/verify-password-pii', {
        password,
        reason: reason.trim(),
        resourceType,
        resourceId,
      });

      if (response.data.success && response.data.data) {
        // Password verified, PII token issued, event logged in audit logs

        const piiToken: PIIAccessToken = {
          token: response.data.data.piiAccessToken,
          expiresAt: response.data.data.expiresAt,
          expiresIn: response.data.data.expiresIn,
        };

        // Clear form
        setPassword('');
        setReason('');

        // Pass token to parent
        onSuccess(piiToken);
      } else {
        setError(response.data.error || 'Invalid password');
      }
    } catch (err: any) {
      console.error('❌ Password verification failed:', err);
      if (err.response?.status === 401) {
        setError('Invalid password. Please try again.');
      } else if (err.response?.status === 429) {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(err.response?.data?.error || 'Failed to verify password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPassword('');
      setReason('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SecurityIcon color="primary" />
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }} icon={<LockIcon />}>
          <Typography variant="body2" fontWeight={600}>
            {description}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            • Your password verification will be logged in audit logs
            <br />
            • Access token is valid for 5 minutes only
            <br />
            • Unauthorized access attempts are tracked and reported
          </Typography>
        </Alert>

        <TextField
          autoFocus
          fullWidth
          type={showPassword ? 'text' : 'password'}
          label="Your Password *"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError('');
          }}
          disabled={loading}
          error={!!error && !reason}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={loading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Reason for Access *"
          placeholder="e.g., Reviewing identity documents for verification approval"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setError('');
          }}
          disabled={loading}
          error={!!error && !!password}
          helperText={error || 'This reason will be recorded in the audit log'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                <DescriptionIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !password.trim() || !reason.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <SecurityIcon />}
        >
          {loading ? 'Verifying...' : 'Verify & Access'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordReAuthDialog;
